import { Editor } from "@tinymce/tinymce-react";
import React, { ComponentProps, useState } from "react";
import { Editor as TinyMCEEditor } from "tinymce";
import "./font-wrapper.css";
import { PdfHeaderFooterModal } from "./pdf-options/pdf-header-footer-modal";

type VpdDesignEditorProps = {
  outputDesignId: number;
  templateString: string;
  handleEditorInit: (editor: TinyMCEEditor) => void;
  setDirty?: (dirty: boolean) => void;
  overrideOpts?: ComponentProps<typeof Editor>;
  removeTemplateMargin?: boolean;
};

export const VpdDesignEditor: React.FC<VpdDesignEditorProps> = ({
  outputDesignId,
  templateString,
  handleEditorInit,
  setDirty,
  overrideOpts,
  removeTemplateMargin,
}) => {
  const [showHeaderFooterModal, setShowHeaderFooterModal] = useState(false);
  const styles = `
@import url('https://fonts.googleapis.com/css2?family=Carlito:ital,wght@0,400;0,700;1,400;1,700&display=swap');
body {
   padding: ${removeTemplateMargin ? "0px" : "30px 96px 30px 96px"};
   content_style: "body { font-family: Arial; }";
}
ol {
  margin: 0 !important;
  padding:0;
}
ol > li {
  position: relative;
  padding-left: 35px;
}
`;

  return (
    <>
      <PdfHeaderFooterModal
        outputDesignId={outputDesignId}
        open={showHeaderFooterModal}
        onClose={() => setShowHeaderFooterModal(false)}
        onOk={() => {
          setShowHeaderFooterModal(false);
        }}
      />
      <Editor
        tinymceScriptSrc={`https://assets.vpd.law/vpd-ui/tinymce/js/tinymce/tinymce.min.js`}
        scriptLoading={{
          async: true,
          defer: true,
        }}
        initialValue={templateString}
        onInit={(_, editor) => {
          handleEditorInit(editor);
        }}
        onDirty={() => setDirty && setDirty(true)}
        onBlur={async (event, editor) => {
          const tree = editor.getContent({
            format: "tree",
          });
          console.log(tree);
        }}
        init={{
          licenseKey: "gpl",
          promotion: false,
          menu: { custom: { title: "PDF Options", items: "headerfooter joinPdfs" } },
          font_family_formats:
            "Andale Mono=andale mono,times; Arial=arial,helvetica,sans-serif; Arial Black=arial black,avant garde; Book Antiqua=book antiqua,palatino; Calibri=Carlito; Calibri Comic Sans MS=comic sans ms,sans-serif; Courier New=courier new,courier; Georgia=georgia,palatino; Helvetica=helvetica; Impact=impact,chicago; Oswald=oswald; Symbol=symbol; Tahoma=tahoma,arial,helvetica,sans-serif; Terminal=terminal,monaco; Times New Roman=times new roman,times; Trebuchet MS=trebuchet ms,geneva; Verdana=verdana,geneva; Webdings=webdings; Wingdings=wingdings,zapf dingbats",
          verify_html: false,
          visualblocks_default_state: false,
          branding: false,
          style_formats: [
            { title: "Bold text", inline: "b" },
            { title: "Red text", inline: "span", styles: { color: "#ff0000" } },
            {
              name: "Inline P",
              title: "inline p tag",
              inline: "p",
              styles: { margin: "0", padding: "0" },
              exact: true,
              block_expand: false,
            },
            {
              title: "Containers",
              items: [
                { title: "section", block: "section", wrapper: true, merge_siblings: false },
                { title: "article", block: "article", wrapper: true, merge_siblings: false },
                { title: "blockquote", block: "blockquote", wrapper: true },
                { title: "hgroup", block: "hgroup", wrapper: true },
                { title: "aside", block: "aside", wrapper: true },
                { title: "figure", block: "figure", wrapper: true },
                {
                  title: "container-div",
                  block: "div",
                  merge_siblings: false,
                  wrapper: true,
                  exact: true,
                  block_expand: false,
                  deep: false,
                },
                {
                  title: "flex-container",
                  block: "div",
                  styles: {
                    display: "flex",
                    ["justify-content"]: "start",
                  },
                  wrapper: true,
                  exact: true,
                },
                {
                  title: "inline-paragraph",
                  inline: "p",
                  wrapper: true,
                  exact: true,
                },
                {
                  title: "flex-item-1",
                  inline: "div",
                  styles: { display: "flex", "flex-grow": "1" },
                  wrapper: true,

                  exact: true,
                },
                {
                  title: "flex-item-2",
                  inline: "div",
                  styles: { display: "flex", "flex-grow": "2" },
                  wrapper: true,
                  merge_siblings: false,
                  exact: true,
                },
                {
                  title: "flex-item-3",
                  inline: "div",
                  styles: { display: "flex", "flex-grow": "3" },
                  wrapper: true,
                  merge_siblings: false,
                  exact: true,
                },
                {
                  title: "flex-item-4",
                  block: "div",
                  styles: { display: "flex", "flex-grow": "4" },
                  wrapper: true,
                  merge_siblings: false,
                  exact: true,
                },
                {
                  title: "flex-item-5",
                  block: "div",
                  styles: { display: "flex", "flex-grow": "5" },
                  wrapper: true,
                  merge_siblings: false,
                  exact: true,
                },
                {
                  title: "flex-break",
                  block: "div",
                  styles: { display: "flex", "flex-basis:": "100%", height: "0" },
                  wrapper: true,
                  merge_siblings: false,
                  exact: true,
                },
              ],
            },
          ],

          style_formats_merge: true,
          end_container_on_empty_block: true,
          content_css: "document",
          adv_list_number_styles: "decimal",
          content_style: styles,
          indent_use_margin: true,
          table_sizing_mode: "fixed",
          table_use_colgroups: false,
          forced_root_block: "false",
          // force_p_newlines: true,
          // force_br_newlines: true,
          indent: false,
          lists_indent_on_tab: true,
          setup: function (ed) {
            ed.on("keydown", function (event) {
              console.log(event.key);

              if (event.key == "Tab") {
                ed.execCommand("Indent");
                event.preventDefault();
                return false;
              }
            });

            ed.ui.registry.addMenuItem("headerfooter", {
              text: "Header Footer",
              onAction: () => setShowHeaderFooterModal(true),
            });
          },
          height: "95%",
          width: "100%",
          resize: false,
          highlight_on_focus: false,
          menubar: "file edit view insert format tools help custom",
          plugins:
            "pagebreak nonbreaking directionality preview save anchor autolink charmap image link lists media searchreplace table visualblocks code",
          toolbar:
            "pagebreaknonbreaking directionality preview save undo redo | blocks fontfamily fontsize | bold italic underline strikethrough |  image table  | align lineheight | checklist numlist bullist indent outdent | charmap | removeformat",
          tinycomments_mode: "embedded",
          tinycomments_author: "Author name",

          ai_request: (
            request: any,
            respondWith: {
              string: (arg0: () => Promise<void>) => any;
            }
          ) => respondWith.string(() => Promise.reject("See docs to implement AI Assistant")),
        }}
        {...overrideOpts}
      />
    </>
  );
};
